// Console Logger for capturing browser console output
// This helps capture console logs, errors, and warnings for debugging

class ConsoleLogger {
  constructor() {
    this.logs = [];
    this.isEnabled = process.env.NODE_ENV === 'development';
    this.maxLogs = 1000; // Keep last 1000 logs
    
    if (this.isEnabled) {
      this.setupInterceptors();
    }
  }

  setupInterceptors() {
    // Check if we're in a browser environment (not SSR)
    if (typeof window === 'undefined') {
      return; // Skip setup during server-side rendering
    }

    // Store original console methods
    const originalLog = console.log;
    const originalError = console.error;
    const originalWarn = console.warn;
    const originalInfo = console.info;
    const originalDebug = console.debug;

    // Intercept console.log
    console.log = (...args) => {
      this.addLog('log', args);
      originalLog.apply(console, args);
    };

    // Intercept console.error
    console.error = (...args) => {
      this.addLog('error', args);
      originalError.apply(console, args);
    };

    // Intercept console.warn
    console.warn = (...args) => {
      this.addLog('warn', args);
      originalWarn.apply(console, args);
    };

    // Intercept console.info
    console.info = (...args) => {
      this.addLog('info', args);
      originalInfo.apply(console, args);
    };

    // Intercept console.debug
    console.debug = (...args) => {
      this.addLog('debug', args);
      originalDebug.apply(console, args);
    };

    // Also capture unhandled errors (only in browser)
    window.addEventListener('error', (event) => {
      this.addLog('unhandled_error', [event.error || event.message]);
    });

    window.addEventListener('unhandledrejection', (event) => {
      this.addLog('unhandled_promise_rejection', [event.reason]);
    });
  }

  addLog(level, args) {
    const logEntry = {
      level,
      message: args.map(arg => {
        if (typeof arg === 'object') {
          try {
            return JSON.stringify(arg);
          } catch (e) {
            return arg.toString();
          }
        }
        return String(arg);
      }).join(' '),
      timestamp: new Date().toISOString(),
      stack: new Error().stack
    };

    this.logs.push(logEntry);

    // Keep only the last maxLogs entries
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(-this.maxLogs);
    }
  }

  getLogs(level = null) {
    if (level) {
      return this.logs.filter(log => log.level === level);
    }
    return this.logs;
  }

  getErrors() {
    return this.getLogs('error');
  }

  getWarnings() {
    return this.getLogs('warn');
  }

  getRecentLogs(count = 50) {
    return this.logs.slice(-count);
  }

  clearLogs() {
    this.logs = [];
  }

  exportLogs() {
    return JSON.stringify(this.logs, null, 2);
  }

  getSummary() {
    const summary = {
      total: this.logs.length,
      errors: this.getErrors().length,
      warnings: this.getWarnings().length,
      logs: this.getLogs('log').length,
      info: this.getLogs('info').length,
      debug: this.getLogs('debug').length,
      unhandled_errors: this.getLogs('unhandled_error').length,
      unhandled_promise_rejections: this.getLogs('unhandled_promise_rejection').length
    };

    return summary;
  }
}

// Create a global instance
const consoleLogger = new ConsoleLogger();

// Export for use in components
export default consoleLogger;

// Export a helper to get console debug info for sharing
export const getConsoleDebugInfo = () => {
  return {
    logs: consoleLogger.getRecentLogs(100), // Last 100 logs
    summary: consoleLogger.getSummary(),
    errors: consoleLogger.getErrors(),
    warnings: consoleLogger.getWarnings()
  };
};

